import * as React from 'react';

import { Formik, FormikConfig } from 'formik';
import { Redirect } from 'react-router';
import { GeocodeResult } from '../../contexts/GeocoderContext';

import { SubmitButton } from '../../components/SubmitButton';
import { Button, ButtonLink } from '../../components/Button';
import { useTitle } from '../../useTitle';
import {
  useSetMatchLocationMutation,
  LocationInput,
  FullMatchFragment,
} from '../../generated/graphql';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useAlert } from '../../contexts/AlertContext';
import { MatchLocation } from '../../components/MatchLocation';
import { paths } from '../../paths';
import { LocationChooser } from './components/LocationChooser';

interface Props {
  match: Pick<FullMatchFragment, 'id' | 'team'>;
}

interface Fields {
  location: GeocodeResult | undefined;
}

const sanitizeMatchLocation = (location: GeocodeResult): LocationInput => {
  const [lat, lng] = location.coords;
  return {
    address: location.address,
    lat,
    lng,
  };
};

const initialValues: Fields = {
  location: undefined,
};

export const SetLocation = ({ match }: Props) => {
  useTitle('Match Location');

  const analytics = useAnalytics();
  const alert = useAlert();
  const [updateMatchLocationResponse, updateMatchLocation] =
    useSetMatchLocationMutation();

  const onSubmit: FormikConfig<Fields>['onSubmit'] = React.useCallback(
    (values) => {
      const formEventMeta = { form: 'set_location' };
      analytics.track('Form Submitted', formEventMeta);

      if (!values.location) {
        alert('Invalid location', 'danger');
        return;
      }

      return updateMatchLocation({
        matchId: match.id,
        location: sanitizeMatchLocation(values.location),
      }).then((response) => {
        if (response.data?.setMatchLocation?.validation) {
          analytics.track('Data Submit Failed', formEventMeta);
          alert('Something went wrong', 'danger');
          return;
        }
        if (response.data?.setMatchLocation?.entity) {
          analytics.track('Data Submit Succeeded', formEventMeta);

          alert('Match updated 🎉');
        }
      });
    },
    [analytics, alert, history, updateMatchLocation],
  );

  if (updateMatchLocationResponse.data?.setMatchLocation?.entity) {
    return <Redirect to={paths.match.home(match.id)} />;
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {(formikProps) => {
        const selectLocation = (location: GeocodeResult | undefined) => {
          formikProps.handleChange('location')(location);
        };

        return (
          <form onSubmit={formikProps.handleSubmit} className="space-y-4">
            {formikProps.values.location ? (
              <>
                <MatchLocation coords={formikProps.values.location.coords} />
                <p>{formikProps.values.location.address}</p>
                <SubmitButton loading={updateMatchLocationResponse.fetching}>
                  Select address
                </SubmitButton>
                <Button
                  type="button"
                  kind="plain"
                  onClick={() => selectLocation(undefined)}
                  icon="x"
                >
                  Clear selection
                </Button>
              </>
            ) : (
              <>
                <LocationChooser
                  selectLocation={selectLocation}
                  teamId={match.team.id}
                />

                <ButtonLink
                  to={paths.match.home(match.id)}
                  kind="plain"
                  icon="x"
                >
                  Cancel
                </ButtonLink>
              </>
            )}
          </form>
        );
      }}
    </Formik>
  );
};
